import type { ReactNode } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { Badge } from '@/components/ui/Badge';
import { TextField } from '@/components/forms/TextField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { formatCurrency, optionLabel, optionLabels } from '@/lib/utils/format.utils';
import {
  FIELD_LABELS,
  INSTRUMENT_SEGMENT_OPTIONS,
  INVESTOR_TYPE_OPTIONS,
  YES_NO_OPTIONS,
} from '@/features/startups/constants';
import { LeadInvestorFields } from '@/features/startups/components/LeadInvestorFields';
import { StartupRaiseIntentFields } from '@/features/startups/components/StartupBusinessFields';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';

const S = UI_STRINGS.STARTUP_PROFILE;

function Fact({
  label,
  value,
  emptyLabel = S.NOT_PROVIDED,
}: {
  label: string;
  value?: string;
  emptyLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt>
        <FieldLabel label={label} variant="caps" />
      </dt>
      <dd className="text-navy text-p6">
        {value ?? <span className="text-slate">{emptyLabel}</span>}
      </dd>
    </div>
  );
}

interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  control?: Control<StartupProfileFormValues>;
  register?: UseFormRegister<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
}

/**
 * Current Raise card: round size, instrument, target close date, valuation,
 * lead investor, use of funds and investor types sought (FR-172).
 */
export function CurrentRaiseSection({
  mode,
  data,
  control,
  register,
  errors,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !control || !register) {
    return (
      <SectionCard title={S.CURRENT_RAISE} headerRight={headerRight}>
        <dl className="flex flex-col gap-4">
          <Fact
            label={S.ROUND_SIZE}
            value={formatCurrency(data.financeSoughtHigh ?? data.financeSoughtLow)}
          />
          <div className="flex flex-col items-start gap-1">
            <dt>
              <FieldLabel label={S.INSTRUMENT} variant="caps" />
            </dt>
            {data.instrument ? (
              <Badge variant="dark">
                {INSTRUMENT_SEGMENT_OPTIONS.find((o) => o.value === data.instrument)?.label ??
                  data.instrument}
              </Badge>
            ) : (
              <p className="text-slate text-p6">{S.NOT_PROVIDED}</p>
            )}
          </div>
          <Fact label={S.TARGET_CLOSE_DATE} value={data.targetCloseDate ?? undefined} />
          <Fact label={S.VALUATION_OR_CAP} value={formatCurrency(data.valuationOrCap)} />
          <Fact
            label={FIELD_LABELS.hasLeadInvestor}
            value={optionLabel(YES_NO_OPTIONS, data.hasLeadInvestor ? 'yes' : 'no')}
          />
          {/* The name only exists alongside a "yes" (UX sheet r151). */}
          {data.hasLeadInvestor && (
            <Fact
              label={S.LEAD_INVESTOR}
              value={data.leadInvestorName ?? undefined}
              emptyLabel={S.LEAD_INVESTOR_NOT_CONFIRMED}
            />
          )}
          <Fact label={FIELD_LABELS.useOfFunds} value={data.useOfFunds || undefined} />
          <Fact
            label={FIELD_LABELS.investorTypesSought}
            value={optionLabels(INVESTOR_TYPE_OPTIONS, data.investorTypesSought)}
          />
        </dl>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={S.CURRENT_RAISE}>
      <div className="flex flex-col gap-6">
        {/* The round's two ends side by side. */}
        <div className="grid gap-5 sm:grid-cols-2">
          <Controller
            control={control}
            name="financeSoughtLow"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={FIELD_LABELS.financeSoughtLow}
                labelVariant="caps"
                prefix="$"
                error={errors?.financeSoughtLow?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="financeSoughtHigh"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={FIELD_LABELS.financeSoughtHigh}
                labelVariant="caps"
                prefix="$"
                error={errors?.financeSoughtHigh?.message}
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel label={S.INSTRUMENT} variant="caps" />
          <Controller
            control={control}
            name="instrument"
            render={({ field }) => (
              <SegmentedControl
                label={S.INSTRUMENT}
                options={INSTRUMENT_SEGMENT_OPTIONS}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        {/* A date and an amount share a row. */}
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            {...register('targetCloseDate')}
            label={S.TARGET_CLOSE_DATE}
            labelVariant="caps"
            type="date"
            error={errors?.targetCloseDate?.message}
          />
          <Controller
            control={control}
            name="valuationOrCap"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={S.VALUATION_OR_CAP}
                labelVariant="caps"
                prefix="$"
                error={errors?.valuationOrCap?.message}
              />
            )}
          />
        </div>
        <Controller
          control={control}
          name="hasLeadInvestor"
          render={({ field }) => (
            <LeadInvestorFields
              hasLeadInvestor={field.value}
              onHasLeadInvestorChange={field.onChange}
              nameInputProps={register('leadInvestorName')}
              nameError={errors?.leadInvestorName?.message}
            />
          )}
        />
        <StartupRaiseIntentFields register={register} errors={errors} />
      </div>
    </SectionCard>
  );
}
